import type { GroupWithStatsResponse } from '@lms/api-client/backend';
import { type SeriesSet, useSeries } from '@lms/charts';
import { Button } from '@lms/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@lms/ui/card';
import { Checkbox } from '@lms/ui/checkbox';
import {
  isLastMonth,
  isNever,
  isNotThisYear,
  isThisMonth,
  isThisYear,
} from '@lms/ui/date-presets';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@lms/ui/dialog';
import { Input } from '@lms/ui/input';
import { SortableTableHead } from '@lms/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@lms/ui/table';
import { TableFilter } from '@lms/ui/table-filter';
import {
  type FilterConfig,
  type SortConfig,
  type UseTableViewResult,
  useTableView,
} from '@lms/ui/use-table-view';
import { useToast } from '@lms/ui/use-toast';
import { DownloadIcon, TrashIcon, XIcon } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLocale, useTranslations } from 'use-intl';
import { CardTableContent } from '@/components/admin/card-table';
import type { Site } from '@/features/branding/brand-provider';
import { EducationTotal } from '@/features/dashboard/components/education-total';
import AddGroup from '@/features/groups/components/add-group';
import OverflowMenu from '@/features/groups/components/overflowmenu';
import { removeGroup } from '@/features/groups/groups-api';
import { useFilteredList } from '@/features/groups/use-filtered-list';
import { useUrlTableState } from '@/lib/use-url-table-state';
import { downloadXlsx, toFileSlug } from '@/lib/xlsx';

interface Selection {
  selected: Set<number>;
  toggle: (ids: number[], checked: boolean) => void;
}

/** Sum of the group's time spent over all curricula, in seconds. */
function totalSeconds(g: GroupWithStatsResponse): number {
  return g.timeSpent.reduce((acc, t) => acc + t.seconds, 0);
}

/** Localized date, or null when the group has never been active. */
function formatDate(
  value: string | null | undefined,
  locale: string,
): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString(locale, { dateStyle: 'medium' });
}

export default function ListView({
  groups,
  site,
  canManage,
  onChanged,
}: {
  site: Site;
  groups: GroupWithStatsResponse[];
  /** Whether the viewer may add, edit, delete or bulk-delete groups. */
  canManage: boolean;
  onChanged: () => void;
}) {
  const t = useTranslations();
  const ta = useTranslations('admin');
  const { toast } = useToast();
  const {
    filtered: searchedGroups,
    onSearchChange,
    setSearch,
    isSearching,
  } = useFilteredList(groups, { getSearchText: (g) => g.name });

  // Shared sort + preset filters, one toolbar governing all three cards.
  const sorts = useMemo<Record<string, SortConfig<GroupWithStatsResponse>>>(
    () => ({
      name: { accessor: (g) => g.name, type: 'string' },
      completedCourses: {
        accessor: (g) => g.completedCourses ?? 0,
        type: 'number',
      },
      timeSpent: { accessor: totalSeconds, type: 'number' },
      lastActive: { accessor: (g) => g.lastActive ?? null, type: 'date' },
    }),
    [],
  );
  const filters = useMemo<Record<string, FilterConfig<GroupWithStatsResponse>>>(
    () => ({
      completed: {
        default: 'all',
        options: {
          all: () => true,
          none: (g) => (g.completedCourses ?? 0) === 0,
          some: (g) => (g.completedCourses ?? 0) > 0,
        },
      },
      active: {
        default: 'any',
        options: {
          any: () => true,
          this_month: (g) => isThisMonth(g.lastActive),
          last_month: (g) => isLastMonth(g.lastActive),
          this_year: (g) => isThisYear(g.lastActive),
          not_this_year: (g) =>
            isNever(g.lastActive) || isNotThisYear(g.lastActive),
        },
      },
    }),
    [],
  );
  const urlConfig = useMemo(
    () => ({
      sortKeys: ['name', 'completedCourses', 'timeSpent', 'lastActive'],
      defaultSort: null,
      filters: {
        completed: { options: ['all', 'none', 'some'], default: 'all' },
        active: {
          options: [
            'any',
            'this_month',
            'last_month',
            'this_year',
            'not_this_year',
          ],
          default: 'any',
        },
      },
    }),
    [],
  );
  const { value, onChange } = useUrlTableState(urlConfig);
  const view = useTableView(searchedGroups, {
    sorts,
    filters,
    value,
    onChange,
  });

  const [selected, setSelected] = useState<Set<number>>(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // One colour per curriculum across every row's time breakdown.
  const timeRows = useMemo(() => groups.flatMap((g) => g.timeSpent), [groups]);
  const curricula = useSeries(
    timeRows,
    (r) => r.curriculumName,
    (r) => r.seconds,
  );

  const visibleGroups = view.rows;
  const tenantGroups = visibleGroups.filter(
    (g) => !g.userCreated && g.groupType === 'tenant',
  );
  const supplierGroups = visibleGroups.filter(
    (g) => !g.userCreated && g.groupType === 'supplier',
  );
  const userCreatedGroups = visibleGroups.filter((g) => g.userCreated);

  const tenantTitle = t(`group_alias.${site.groupAlias}`);
  const supplierTitle = t('group_alias.suppliers');
  const userCreatedTitle = t('admin.user_company_name');

  // Names are unique per site across tenant and supplier groups (user-created
  // companies live in a separate namespace).
  const takenNames = groups.filter((g) => !g.userCreated).map((g) => g.name);

  // Only act on rows the user can currently see, so a search or filter can't
  // hide selected groups that would still be deleted or exported.
  const selectedVisible = visibleGroups.filter((g) => selected.has(g.id));

  const hasQuery = view.isFiltered || isSearching;
  const clearAll = () => {
    view.reset();
    setSearch('');
  };

  const selection: Selection = {
    selected,
    toggle: (ids, checked) =>
      setSelected((prev) => {
        const next = new Set(prev);
        for (const id of ids) {
          if (checked) next.add(id);
          else next.delete(id);
        }
        return next;
      }),
  };

  const handleBulkDelete = async () => {
    setIsDeleting(true);
    try {
      const results = await Promise.allSettled(
        selectedVisible.map((g) => removeGroup(g.id)),
      );
      const failed = selectedVisible.filter(
        (_, i) => results[i].status === 'rejected',
      );
      if (failed.length > 0) {
        toast({
          title: t('admin.bulk_delete_failed', { count: failed.length }),
          description: failed.map((g) => g.name).join(', '),
          variant: 'destructive',
        });
      }
      // Keep failed rows selected so the user can retry.
      setSelected(new Set(failed.map((g) => g.id)));
      setConfirmOpen(false);
      onChanged();
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex w-full flex-row flex-wrap items-center gap-3">
        <Input
          onChange={onSearchChange}
          placeholder={t('admin.search')}
          className="max-w-96"
        />
        <TableFilter
          label={ta('courses_completed')}
          value={view.filterValues.completed}
          defaultValue="all"
          onChange={(v) => view.setFilter('completed', v)}
          options={[
            { value: 'all', label: ta('all') },
            { value: 'none', label: ta('none') },
            { value: 'some', label: ta('filter_at_least_one') },
          ]}
        />
        <TableFilter
          label={ta('last_active')}
          value={view.filterValues.active}
          defaultValue="any"
          onChange={(v) => view.setFilter('active', v)}
          options={[
            { value: 'any', label: ta('filter_any') },
            { value: 'this_month', label: ta('filter_this_month') },
            { value: 'last_month', label: ta('filter_last_month') },
            { value: 'this_year', label: ta('filter_this_year') },
            { value: 'not_this_year', label: ta('filter_not_this_year') },
          ]}
        />
        {hasQuery && (
          <Button variant="ghost" size="sm" onClick={clearAll}>
            <XIcon className="mr-1 h-4 w-4" />
            {ta('clear_filters')}
          </Button>
        )}
      </div>
      <GroupTable
        title={tenantTitle}
        description={t('admin.manage', { what: tenantTitle.toLowerCase() })}
        nameHeader={t(`group_alias.${site.groupAlias}_singular`)}
        groups={tenantGroups}
        action={
          canManage ? (
            <AddGroup
              site={site}
              groupType="tenant"
              what={t(`group_alias.${site.groupAlias}_singular`)}
              whatPlural={tenantTitle}
              existingNames={takenNames}
              onCreated={onChanged}
            />
          ) : undefined
        }
        site={site}
        curricula={curricula}
        canManage={canManage}
        selection={selection}
        view={view}
        hasQuery={hasQuery}
        onClearFilters={clearAll}
        onChanged={onChanged}
      />
      <GroupTable
        title={supplierTitle}
        description={t('admin.manage', {
          what: supplierTitle.toLowerCase(),
        })}
        nameHeader={t('group_alias.suppliers_singular')}
        groups={supplierGroups}
        action={
          canManage ? (
            <AddGroup
              site={site}
              groupType="supplier"
              what={t('group_alias.suppliers_singular')}
              whatPlural={supplierTitle}
              existingNames={takenNames}
              onCreated={onChanged}
            />
          ) : undefined
        }
        site={site}
        curricula={curricula}
        canManage={canManage}
        selection={selection}
        view={view}
        hasQuery={hasQuery}
        onClearFilters={clearAll}
        onChanged={onChanged}
      />
      <GroupTable
        title={userCreatedTitle}
        description={t('admin.user_company_description')}
        nameHeader={t('admin.company_name')}
        groups={userCreatedGroups}
        site={site}
        curricula={curricula}
        canManage={canManage}
        selection={selection}
        view={view}
        hasQuery={hasQuery}
        onClearFilters={clearAll}
        onChanged={onChanged}
      />

      {canManage && selectedVisible.length > 0 && (
        <div className="bg-background sticky bottom-6 z-40 mx-auto flex w-fit items-center gap-3 rounded-full border py-2 pr-2 pl-5 shadow-lg">
          <span className="text-sm font-medium whitespace-nowrap">
            {t('admin.selected_count', { count: selectedVisible.length })}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="rounded-full"
            onClick={() => setSelected(new Set())}
          >
            <XIcon className="mr-1 h-4 w-4" />
            {t('admin.clear_selection')}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            className="rounded-full"
            onClick={() => setConfirmOpen(true)}
          >
            <TrashIcon className="mr-1 h-4 w-4" />
            {t('admin.delete_selected')}
          </Button>
        </div>
      )}

      {canManage && (
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('admin.are_you_sure')}</DialogTitle>
              <DialogDescription>
                {t('admin.bulk_delete_warning', {
                  count: selectedVisible.length,
                })}
              </DialogDescription>
            </DialogHeader>
            <ul className="max-h-48 list-inside list-disc overflow-y-auto text-sm">
              {selectedVisible.map((g) => (
                <li key={g.id}>{g.name}</li>
              ))}
            </ul>
            <DialogFooter>
              <Button
                variant="destructive"
                className="w-full"
                onClick={handleBulkDelete}
                disabled={isDeleting || selectedVisible.length === 0}
              >
                <TrashIcon className="mr-2 h-4 w-4" />
                {t('admin.delete_selected')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function GroupTable({
  title,
  description,
  nameHeader,
  groups,
  action,
  site,
  curricula,
  canManage,
  selection,
  view,
  hasQuery,
  onClearFilters,
  onChanged,
}: {
  title: string;
  description: string;
  nameHeader: string;
  groups: GroupWithStatsResponse[];
  /** Extra header button(s), e.g. the card's "Add" button. */
  action?: ReactNode;
  site: Site;
  curricula: SeriesSet;
  /** Whether the viewer may select rows and open the row overflow menu. */
  canManage: boolean;
  selection: Selection;
  view: UseTableViewResult<GroupWithStatsResponse>;
  /** True when a search or filter is narrowing the list. */
  hasQuery: boolean;
  onClearFilters: () => void;
  onChanged: () => void;
}) {
  const t = useTranslations('admin');
  const ts = useTranslations('stats');
  const locale = useLocale();
  const ids = groups.map((g) => g.id);
  const selectedHere = ids.filter((id) => selection.selected.has(id)).length;
  const handleExport = () => {
    const rows = [
      [
        nameHeader,
        t('unit_id'),
        t('courses_completed'),
        `${ts('course_time')} (min)`,
        t('last_active'),
      ],
      ...groups.map((g) => [
        g.name,
        g.unitId ?? '',
        g.completedCourses ?? 0,
        Math.round(totalSeconds(g) / 60),
        formatDate(g.lastActive, locale) ?? '',
      ]),
    ];
    const date = new Date().toISOString().slice(0, 10);
    void downloadXlsx(
      `${site.slug}-${toFileSlug(title)}-${date}.xlsx`,
      title,
      rows,
      site.primaryColor,
    );
  };
  const headerState =
    selectedHere === 0
      ? false
      : selectedHere === ids.length
        ? true
        : 'indeterminate';

  return (
    <Card className="col-span-1 overflow-hidden md:col-span-3">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="flex flex-col space-y-1.5">
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
        <div className="flex shrink-0 flex-row items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={groups.length === 0}
          >
            <DownloadIcon className="mr-2 h-4 w-4" />
            {t('export_xlsx')}
          </Button>
          {action}
        </div>
      </CardHeader>
      <CardTableContent>
        <Table>
          <TableHeader>
            <TableRow>
              {canManage && (
                <TableHead className="w-10">
                  <Checkbox
                    checked={headerState}
                    disabled={ids.length === 0}
                    onCheckedChange={(checked) =>
                      selection.toggle(ids, checked === true)
                    }
                    aria-label={t('select_all')}
                  />
                </TableHead>
              )}
              <TableHead className="max-w-10"></TableHead>
              <SortableTableHead
                sortKey="name"
                direction={view.sortDirFor('name')}
                onSort={view.toggleSort}
              >
                {nameHeader}
              </SortableTableHead>
              <SortableTableHead
                sortKey="completedCourses"
                direction={view.sortDirFor('completedCourses')}
                onSort={view.toggleSort}
              >
                {t('courses_completed')}
              </SortableTableHead>
              <SortableTableHead
                sortKey="timeSpent"
                direction={view.sortDirFor('timeSpent')}
                onSort={view.toggleSort}
              >
                {ts('course_time')}
              </SortableTableHead>
              <SortableTableHead
                sortKey="lastActive"
                direction={view.sortDirFor('lastActive')}
                onSort={view.toggleSort}
              >
                {t('last_active')}
              </SortableTableHead>
              {canManage && <TableHead></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.length === 0 && hasQuery ? (
              <TableRow>
                <TableCell
                  colSpan={canManage ? 7 : 5}
                  className="text-muted-foreground py-8 text-center"
                >
                  <div className="flex flex-col items-center gap-2">
                    <span>{t('no_matches')}</span>
                    <Button variant="ghost" size="sm" onClick={onClearFilters}>
                      <XIcon className="mr-1 h-4 w-4" />
                      {t('clear_filters')}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              groups.map((group) => (
                <GroupRow
                  group={group}
                  site={site}
                  curricula={curricula}
                  key={group.id}
                  canManage={canManage}
                  selection={selection}
                  onChanged={onChanged}
                />
              ))
            )}
          </TableBody>
        </Table>
      </CardTableContent>
    </Card>
  );
}

function GroupRow({
  group,
  site,
  curricula,
  canManage,
  selection,
  onChanged,
}: {
  group: GroupWithStatsResponse;
  site: Site;
  curricula: SeriesSet;
  /** Whether to show the selection checkbox and the row overflow menu. */
  canManage: boolean;
  selection: Selection;
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const locale = useLocale();
  const isSelected = selection.selected.has(group.id);
  const href = `/${site.slug}/admin/groups/${group.id}`;
  const lastActive = formatDate(group.lastActive, locale);

  // The whole row opens the group. The name stays a real <Link> for keyboard
  // and middle-click; clicks on it are left to the link itself.
  const onRowClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('a')) return;
    if (window.getSelection()?.toString()) return; // don't hijack text selection
    if (e.metaKey || e.ctrlKey) {
      window.open(href, '_blank', 'noopener');
    } else {
      navigate(href);
    }
  };

  return (
    <TableRow
      data-state={isSelected ? 'selected' : undefined}
      className="cursor-pointer"
      onClick={onRowClick}
    >
      {/* The checkbox cell toggles selection instead of navigating. */}
      {canManage && (
        <TableCell
          onClick={(e) => {
            e.stopPropagation();
            selection.toggle([group.id], !isSelected);
          }}
        >
          <Checkbox
            checked={isSelected}
            onClick={(e) => e.stopPropagation()}
            onCheckedChange={(checked) =>
              selection.toggle([group.id], checked === true)
            }
            aria-label={group.name}
          />
        </TableCell>
      )}
      <TableCell>
        {group.logo && (
          <img
            src={group.logo}
            alt={`${group.name} logotyp`}
            className="max-h-8 max-w-10"
          />
        )}
      </TableCell>
      <TableCell className="font-medium">
        <Link to={href}>{group.name}</Link>
      </TableCell>
      <TableCell>{group.completedCourses ?? 0}</TableCell>
      <TableCell>
        {group.timeSpent.length > 0 ? (
          <EducationTotal
            totals={group.timeSpent}
            seriesSet={curricula}
            locale={locale}
          />
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {lastActive ?? '—'}
      </TableCell>
      {/* React bubbles events from the menu's portalled dialogs through here. */}
      {canManage && (
        <TableCell
          className="cursor-default text-right"
          onClick={(e) => e.stopPropagation()}
        >
          <OverflowMenu group={group} site={site} onChanged={onChanged} />
        </TableCell>
      )}
    </TableRow>
  );
}
